import React from 'react';
import { Form, Button, InputGroup, ButtonGroup } from 'react-bootstrap';
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap } from 'react-leaflet';
import { FaMapMarkerAlt } from 'react-icons/fa';
import Slider from 'rc-slider';
import L from 'leaflet';
import { renderToString } from 'react-dom/server';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { OpenStreetMapProvider } from 'leaflet-geosearch';


const customDivIcon = L.divIcon({
  html: renderToString(
    <FontAwesomeIcon
      icon={faLocationDot}
      style={{ fontSize: 32, color: '#e03131' }} 
    />
  ),
  className: 'fa-marker',
  iconSize: [32, 32],
  iconAnchor: [16, 32],  
  popupAnchor: [0, -28],
});

const MapUpdater = ({ center }) => {
  const map = useMap();
  if (center) {
    map.setView(center, map.getZoom(), { animate: true });
  }
  return null;
};

const GeoSearchInput = ({ onSelect, disabled }) => {
  const [query, setQuery] = React.useState('');
  const provider = new OpenStreetMapProvider();

  const handleSearch = async () => {
    if (!query || disabled) return;
    const results = await provider.search({ query });
    if (results && results.length > 0) {
      const { x, y, label } = results[0];
      onSelect({ lat: y, lng: x, label });
    } else {
      alert('Location not found');
    }
  };

  return (
    <InputGroup className="mb-3">
      <Form.Control
        placeholder="Search location..."
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
        disabled={disabled}
      />
      <InputGroup.Text onClick={handleSearch} style={{ cursor: 'pointer', opacity: disabled ? 0.5 : 1 }}>
        <FaMapMarkerAlt />
      </InputGroup.Text>
    </InputGroup>
  );
};

const SegmentMap = ({ enabled, locationRadius, measurementSystem, onSegmentChange, position, setEnabled }) => {
  const radiusInMeters =
    measurementSystem === 'imperial' ? locationRadius * 1609 : locationRadius * 1000;

  const center = position || { lat: 40.7, lng: -73.9, label: 'New York' };

  const handleChange = (field, value) => {
    if (onSegmentChange) {
      onSegmentChange(field, value);
    }
  };

  const handleLocationSelect = (pos) => {
    handleChange('position', pos);
  };

  return (
    <div style={{ display: 'flex', width: '100%', marginTop: '2rem', height: '500px', borderRadius: '12px' }}>
      {/* Left card for controls */}
      <div
        style={{
          flex: '0 0 300px',
          backgroundColor: '#fff',
          padding: 20,
          borderRadius: 12,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          overflowY: 'auto',
          height: '80%',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h5>Define Area By</h5>
          <Form.Check
            id="locationEnabled"
            type="checkbox"
            label="Enable"
            checked={enabled}
            onChange={() => setEnabled && setEnabled(!enabled)}
          />
        </div>

        <ButtonGroup className="w-100">
          <Button variant="outline-secondary" active disabled={!enabled}>
            Radius
          </Button>
          <Button variant="outline-secondary" disabled>
            Region
          </Button>
        </ButtonGroup>

        <GeoSearchInput onSelect={handleLocationSelect} disabled={!enabled} />

        <div>
          <h6>System of Measurement</h6>
          <ButtonGroup className="w-100">
            <Button
              variant={measurementSystem === 'metric' ? 'secondary' : 'outline-secondary'}
              onClick={() => handleChange('measurementSystem', 'metric')}
              disabled={!enabled}
            >
              Metric (km)
            </Button>
            <Button
              variant={measurementSystem === 'imperial' ? 'secondary' : 'outline-secondary'}
              onClick={() => handleChange('measurementSystem', 'imperial')}
              disabled={!enabled}
            >
              Imperial (mi)
            </Button>
          </ButtonGroup>
        </div>

        <div>
          <h6>Edit Target Radius</h6>
          <Slider
            min={0}
            max={100}
            step={10}
            value={locationRadius}
            onChange={val => handleChange('locationRadius', val)}
            marks={{
              0: '0',
              10: '10',
              20: '20',
              30: '30',
              40: '40',
              50: '50',
              60: '60',
              70: '70',
              80: '80',
              90: '90',
              100: '100'
            }}
            style={{ color: '#6c4cc4' }}
            disabled={!enabled}
          />
        </div>
      </div>

      {/* Map on the right */}
      <MapContainer
        center={[center.lat, center.lng]} zoom={11} style={{ flex: 1, borderRadius: '12px' }}
      >
        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
        <MapUpdater center={[center.lat, center.lng]} />
        <Circle
          center={[center.lat, center.lng]}
          radius={radiusInMeters}
          pathOptions={{ color: '#6c4cc4', fillColor: '#6c4cc4', fillOpacity: 0.2 }}
        />
        <Marker position={[center.lat, center.lng]} icon={customDivIcon}>
          <Popup>{center.label || 'Selected Location'}</Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

export default SegmentMap;

